import { z } from "zod";

// OKLCH color string validation regex
// Matches formats like: 'oklch(1 0 0)', 'oklch(0.141 0.005 285.823)', 'oklch(1 0 0 / 10%)'
const oklchColorRegex =
  /^oklch\(\s*[\d.]+\s+[\d.]+\s+[\d.]*\s*(?:\/\s*[\d.]+%?)?\s*\)$/;

// Zod schema for OKLCH color strings
const OklchColor = z.string().regex(oklchColorRegex, {
  message:
    "Must be a valid OKLCH color format (e.g., 'oklch(1 0 0)' or 'oklch(0.141 0.005 285.823)')",
});

/**
 * One mode's colour tokens — the shadcn set, which the components use, the
 * model knows and shadcn's own theme generators emit.
 *
 * A `Theme` holds one of these per mode (see `theme/theme.ts`); this is the
 * shape of a single mode's half, and what every reader of a resolved theme
 * — the parser, the property panel, the prompt — consumes.
 */
export const ColorTokens = z.object({
  // Background colors
  background: OklchColor,
  foreground: OklchColor,

  // Card colors
  card: OklchColor,
  cardForeground: OklchColor,

  // Popover colors
  popover: OklchColor,
  popoverForeground: OklchColor,

  // Primary colors
  primary: OklchColor,
  primaryForeground: OklchColor,

  // Secondary colors
  secondary: OklchColor,
  secondaryForeground: OklchColor,

  // Muted colors
  muted: OklchColor,
  mutedForeground: OklchColor,

  // Accent colors
  accent: OklchColor,
  accentForeground: OklchColor,

  // Destructive colors
  destructive: OklchColor,

  // Border and input colors
  border: OklchColor,
  input: OklchColor,
  ring: OklchColor,

  // Chart colors
  chart1: OklchColor,
  chart2: OklchColor,
  chart3: OklchColor,
  chart4: OklchColor,
  chart5: OklchColor,

  // Sidebar colors
  sidebar: OklchColor,
  sidebarForeground: OklchColor,
  sidebarPrimary: OklchColor,
  sidebarPrimaryForeground: OklchColor,
  sidebarAccent: OklchColor,
  sidebarAccentForeground: OklchColor,
  sidebarBorder: OklchColor,
  sidebarRing: OklchColor,
});
export type ColorTokens = z.infer<typeof ColorTokens>;

/**
 * A resolved mode of a theme: its colours, plus the radius the whole theme
 * states.
 *
 * What `themeVariant(theme, mode)` flattens a ref's theme down to, for the
 * parser, the property panel and the prompt — none of which want a whole theme.
 */
export const ThemeVariant = ColorTokens.extend({
  radius: z.string().optional(),
});
export type ThemeVariant = z.infer<typeof ThemeVariant>;
export type ThemeColorTokenName = keyof ColorTokens;

// The same set at runtime, derived from the schema so the two can't drift.
export const THEME_COLOR_TOKEN_NAMES = Object.keys(
  ColorTokens.shape,
) as ThemeColorTokenName[];

export const TAILWIND_COLOR_NAMES = [
  "red",
  "orange",
  "amber",
  "yellow",
  "lime",
  "green",
  "emerald",
  "teal",
  "cyan",
  "sky",
  "blue",
  "indigo",
  "violet",
  "purple",
  "fuchsia",
  "pink",
  "rose",
  "slate",
  "gray",
  "zinc",
  "neutral",
  "stone",
  "mauve",
  "olive",
  "mist",
  "taupe",
] as const;

export const TAILWIND_COLOR_SHADES = [
  "50",
  "100",
  "200",
  "300",
  "400",
  "500",
  "600",
  "700",
  "800",
  "900",
  "950",
] as const;

export type TailwindColorName = (typeof TAILWIND_COLOR_NAMES)[number];
export type TailwindColorShade = (typeof TAILWIND_COLOR_SHADES)[number];
export type TailwindBasicColorName = "black" | "white";
export type TailwindColorTokenName =
  | `${TailwindColorName}${TailwindColorShade}`
  | TailwindBasicColorName;
export type ColorTokenName = ThemeColorTokenName | TailwindColorTokenName;

// Shadcn color themes
export const ColorTheme = z.enum([
  "blue",
  "blueDark",
  "default",
  "defaultDark",
  "ice",
  "iceDark",
  "grayscale",
  "grayscaleDark",
  "green",
  "greenDark",
  "orange",
  "orangeDark",
  "red",
  "redDark",
  "rose",
  "roseDark",
  "violet",
  "violetDark",
  "yellow",
  "yellowDark",
]);
export type ColorTheme = z.infer<typeof ColorTheme>;
